import { useRef, type FC, type ReactNode, type RefObject } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useFavoritesItem } from './useFavoritesItem.tsx';

import { type FavoritesItemProvider_item$key } from './__generated__/FavoritesItemProvider_item.graphql.ts';
import { type FavoritesItemProvider_user$key } from './__generated__/FavoritesItemProvider_user.graphql.ts';

const itemFragment = graphql`
    fragment FavoritesItemProvider_item on Item {
        ...useFavoritesItem_item
    }
`;
const userFragment = graphql`
    fragment FavoritesItemProvider_user on User {
        ...useFavoritesItem_user
    }
`;

export type FavoritesItemPropsType = {
    isFolderVisible: boolean;
    showFolderPlusIcon?: boolean;
    onFavorited?: () => void;
    onUnFavorited?: () => void;
    onFolderActionStart?: () => void;
    onFolderActionEnd?: () => void;
};

type Props = {
    item: FavoritesItemProvider_item$key | null | undefined;
    user: FavoritesItemProvider_user$key | null | undefined;
    applyMouseEvents?: boolean;
    children: (args: FavoritesItemPropsType) => ReactNode;
    className?: string;
    itemId?: string;
    userId?: string;
    tileElementRef?: RefObject<HTMLDivElement | null>;
};

const FavoritesItemProvider: FC<Props> = ({
    item: itemRef,
    user: userRef,
    applyMouseEvents = false,
    className,
    itemId,
    userId,
    children,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const tileElementRef = useRef(null);
    const {
        isFolderVisible,
        showFolderPlusIcon,
        onFavorited,
        onUnFavorited,
        onFolderActionStart,
        onFolderActionEnd,
        onMouseEnter,
        onMouseLeave,
    } = useFavoritesItem({ item, user, itemId, userId, applyMouseEvents, tileElementRef });
    return (
        <div
            className={className}
            ref={tileElementRef}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {children({
                isFolderVisible,
                showFolderPlusIcon,
                onFavorited,
                onUnFavorited,
                onFolderActionStart,
                onFolderActionEnd,
            })}
        </div>
    );
};

export default FavoritesItemProvider;
